/**
 * Approvals Page
 * Centralized approval dashboard with two tabs:
 * 1. Contract Approvals - Contract lifecycle actions requiring approval,
 *    including new contracts, finalisations, adhoc amendments, and rate
 *    changes (both adhoc and bulk-initiated). Filter chips narrow the
 *    list by change-type cluster.
 * 2. Verifications - Post-audit items for review.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  HelpOutline as QueryIcon,
  Undo as ReverseIcon,
  Description as ContractIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachmentIcon,
  TrendingUp as RateChangeIcon,
  Reply as ReplyIcon,
  Approval as ApprovalIcon,
  CheckCircle as FinalisationIcon,
  Tune as AmendmentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost } from '../utils/apiClient';
import ApprovalDetailModal from '../components/approvals/ApprovalDetailModal';
import TabPanel from '../components/common/TabPanel';
import { usePermissions } from '../contexts/PermissionContext';
import RateChangePreviewModal from '../components/contracts/RateChangePreviewModal';
import ContractReferenceCell from '../components/contracts/ContractReferenceCell';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import useTableSort from '../hooks/useTableSort';
import { getStatusColor, getPriorityColor } from '../utils/statusColors';

// Category mapping for tabs
const categoryByTab = ['contract', 'verification'];

// Change-type filter chips for the Contract Approvals tab. Each entry maps to
// a CSV passed to GET /approvals?change_type=...; `null` means no filter.
// countKey is the matching column in fn_get_pending_counts so the chip can
// surface a live pending count alongside its label.
// Each entry doubles as the "Filter by type" stat card on the approvals
// dashboard and as the change_type filter for the table below.
//   value     - CSV passed to GET /approvals?change_type=...; null means no filter
//   countKey  - field on fn_get_pending_counts response surfaced as the big number
//   color     - accent colour for the icon tile and the active-card border
//   getIcon   - returns the MUI icon component to render in the card tile
const TYPE_FILTERS = [
  { key: 'all',                value: null,                                   label: 'All Approvals',    subtitle: 'All change types',         countKey: 'contract_approvals', color: '#1976d2', getIcon: () => ApprovalIcon },
  { key: 'contract_approvals', value: 'contract_approval',                    label: 'New Contracts',    subtitle: 'Pending approval',         countKey: 'new_contracts',      color: '#0088FE', getIcon: () => ContractIcon },
  { key: 'rate_changes',       value: 'bulk_rate_change,adhoc_rate_change',   label: 'Rate Changes',     subtitle: 'Bulk & adhoc',             countKey: 'rate_changes',       color: '#00C49F', getIcon: () => RateChangeIcon },
  { key: 'finalisations',      value: 'contract_finalisation',                label: 'Finalisations',    subtitle: 'Maturity payouts',         countKey: 'finalisations',      color: '#FFBB28', getIcon: () => FinalisationIcon },
  { key: 'amendments',         value: 'adhoc_capital_withdrawal,adhoc_capital_injection,adhoc_interest_payment,schedule_override,early_termination,contract_rollover,contract_extension', label: 'Other Amendments', subtitle: 'Capital, schedule, etc', countKey: 'amendments', color: '#FF8042', getIcon: () => AmendmentIcon },
];

const RATE_CHANGE_TYPES = new Set(['bulk_rate_change', 'adhoc_rate_change']);

// Build a {change_type -> {key, label}} lookup from TYPE_FILTERS so the
// bulk-approve dialog can group a heterogeneous selection by cluster
// without duplicating cluster definitions. Skips the 'all' entry which
// has no value.
const CHANGE_TYPE_TO_CLUSTER = (() => {
  const map = {};
  TYPE_FILTERS.forEach((f) => {
    if (!f.value) return;
    f.value.split(',').forEach((ct) => {
      map[ct.trim()] = { key: f.key, label: f.label, color: f.color };
    });
  });
  return map;
})();

// Fallback cluster colour for any change_type we haven't explicitly clustered
// — pulled from TYPE_FILTERS so the colour stays in sync with the stat cards.
const AMENDMENT_FALLBACK = TYPE_FILTERS.find((f) => f.key === 'amendments');

// Map a change_type string to its cluster entry. Returns the 'amendments'
// fallback for any unknown type so a future change_type without a TYPE_FILTERS
// entry still counts toward the mixed-cluster check (errs on the side of
// flagging, not silently approving).
function clusterForChangeType(changeType) {
  return CHANGE_TYPE_TO_CLUSTER[changeType] || {
    key: 'amendments',
    label: 'Other Amendments',
    color: AMENDMENT_FALLBACK?.color,
  };
}

// Dashboard-style stat card. Click-to-filter when onClick is provided; the
// active filter gets an accented border so it reads as "selected".
function StatCard({ title, value, subtitle, icon: Icon, color, onClick, selected }) {
  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        border: selected ? `2px solid ${color}` : '2px solid transparent',
        boxShadow: selected ? 3 : 1,
        '&:hover': onClick ? { boxShadow: 4 } : {},
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {Icon && (
            <Box sx={{ backgroundColor: `${color}20`, borderRadius: 2, p: 1 }}>
              <Icon sx={{ color, fontSize: 32 }} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function ApprovalsPage() {
  const { currentUserEmail } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [summary, setSummary] = useState({
    contract_approvals: 0,
    new_contracts: 0,
    rate_changes: 0,
    finalisations: 0,
    amendments: 0,
    verifications: 0,
    total: 0,
  });
  const [permissions, setPermissions] = useState({
    tab_contract_approvals: 'none',
    tab_verifications: 'none',
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionComments, setActionComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [hasSynced, setHasSynced] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const sort = useTableSort({ defaultSort: [{ column: 'requested_at', dir: 'asc' }] });
  const [rateChangePreviewDialog, setRateChangePreviewDialog] = useState({ open: false, rateChangeId: null, currencyCode: 'ZAR' });
  // Selected pending_change ids for the bulk-approve action bar.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Bulk-approve confirmation dialog state — opens when the user clicks
  // "Approve Selected" in the bulk action bar, captures optional comments
  // shared across every approval in the batch. When the selection spans
  // more than one cluster (mixed=true) a typed-count confirmation guard
  // is required before the Approve button enables.
  const [bulkApproveDialog, setBulkApproveDialog] = useState({
    open: false,
    comments: '',
    clusters: [],        // [{ key, label, count }] derived at open time
    mixed: false,
    confirmInput: '',    // user types the total count to confirm mixed approvals
  });

  // Reset row selection when filters change so the user doesn't carry stale
  // selections that no longer correspond to visible rows.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, statusFilter, typeFilter, debouncedSearch]);

  // Debounce search input so every keystroke doesn't hit the API
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-sync on initial page load
  useEffect(() => {
    const autoSync = async () => {
      if (hasSynced) return;
      setHasSynced(true);
      try {
        await apiPost('/approvals/sync-pending-contracts');
      } catch (error) {
        // Silent fail for auto-sync - user can manually sync if needed
      }
    };
    autoSync();
  }, [hasSynced]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load permissions first
      const permResponse = await apiGet('/approvals/permissions');
      if (permResponse.success) {
        setPermissions(permResponse.data);
      }

      // Load summary
      const summaryResponse = await apiGet('/approvals/summary');
      if (summaryResponse.success) {
        setSummary(summaryResponse.data);
      }

      const category = categoryByTab[activeTab];
      // Map filter values to actual status values
      let statusParam = '';
      if (statusFilter === 'pending') {
        statusParam = 'pending,pending_review';
      } else if (statusFilter === 'queried') {
        statusParam = 'queried';
      } else if (statusFilter === 'executed') {
        statusParam = 'executed,approved,verified';
      } else if (statusFilter === 'rejected') {
        statusParam = 'rejected,reversed,cancelled';
      }
      const params = new URLSearchParams({ category, status: statusParam });
      if (debouncedSearch) params.set('search', debouncedSearch);
      // Type filter is only meaningful on the Contract Approvals tab.
      if (activeTab === 0) {
        const typeCsv = TYPE_FILTERS.find(t => t.key === typeFilter)?.value;
        if (typeCsv) params.set('change_type', typeCsv);
      }
      if (sort.apiParams.sort_by) {
        params.set('sort_by', sort.apiParams.sort_by);
        params.set('sort_dir', sort.apiParams.sort_dir);
      }
      const itemsResponse = await apiGet(`/approvals?${params.toString()}`);
      if (itemsResponse.success) {
        setPendingChanges(itemsResponse.data);
      }
    } catch (error) {
      console.error('Error loading approvals data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, typeFilter, debouncedSearch, sort.apiParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTabChange = (event, newValue) => {
    // Reset filter to 'pending' if leaving Verifications tab while on 'queried'
    if (statusFilter === 'queried' && newValue !== 1) {
      setStatusFilter('pending');
    }
    setActiveTab(newValue);
  };

  const handleViewDetails = (item) => {
    if (RATE_CHANGE_TYPES.has(item.change_type)) {
      handlePreviewRateChange(item);
      return;
    }
    setSelectedItem(item);
    setDetailModalOpen(true);
  };

  const handleOpenActionDialog = (item, action) => {
    setSelectedItem(item);
    setActionType(action);
    setActionComments('');
    setActionMessage(null);
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedItem(null);
    setActionType(null);
    setActionComments('');
  };

  const handleExecuteAction = async (directAction, directComments) => {
    const action = directAction || actionType;
    const comments = directComments !== undefined ? directComments : actionComments;

    if (!selectedItem || !action) return;

    // Validate comments for required actions
    if (['reject', 'query', 'reverse', 'respond'].includes(action) && !comments.trim()) {
      return; // Comments required
    }

    setActionLoading(true);
    setActionMessage(null);
    try {
      await apiPost(`/approvals/${selectedItem.id}/${action}`, {
        comments: comments,
      });
      handleCloseActionDialog();
      setDetailModalOpen(false);
      setActionMessage({ type: 'success', text: `Successfully ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : action === 'verify' ? 'verified' : action === 'query' ? 'queried' : action === 'reverse' ? 'reversed' : action === 'respond' ? 'responded to' : action + 'd'}: ${selectedItem.entity_reference || selectedItem.change_summary}` });
      await loadData();
    } catch (error) {
      console.error(`Error executing ${action}:`, error);
      const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'An unexpected error occurred';
      setActionMessage({ type: 'error', text: message });
    } finally {
      setActionLoading(false);
    }
  };

  // Check if user can see a tab
  const canViewTab = (tabKey) => {
    return permissions[tabKey] !== 'none';
  };

  // Check if user can take actions on a tab
  const canActOnTab = (tabKey) => {
    return permissions[tabKey] === 'admin';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd MMM yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  // Open the rich preview modal for rate-change rows; routes through the
  // unified action dialog when the user clicks Approve/Reject inside.
  const handlePreviewRateChange = (item) => {
    const rateChangeId = item.change_data?.rate_change_id;
    if (!rateChangeId) {
      // Fall back to generic detail modal if the link isn't present (defensive).
      handleViewDetails(item);
      return;
    }
    setSelectedItem(item);
    setRateChangePreviewDialog({
      open: true,
      rateChangeId,
      currencyCode: item.change_data?.currency_code || 'ZAR',
    });
  };

  // Render action buttons based on category and permissions
  const renderActionButtons = (item) => {
    const category = categoryByTab[activeTab];
    const tabKey = category === 'contract' ? 'tab_contract_approvals' : 'tab_verifications';
    const canAct = canActOnTab(tabKey);
    const isRateChange = RATE_CHANGE_TYPES.has(item.change_type);
    const openDetail = isRateChange ? () => handlePreviewRateChange(item) : () => handleViewDetails(item);

    if (!canAct) {
      return (
        <Tooltip title="View Details">
          <IconButton size="small" onClick={openDetail}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }

    // Contract Approvals
    if (category === 'contract') {
      if (item.status !== 'pending') return null;

      // Check if current user submitted this change (conflict of interest).
      // pending_changes.requested_by is the authoritative field — change_data.created_by
      // isn't populated for contract_approval changes.
      const submittedBy = item.requested_by || item.change_data?.created_by;
      const isCreator = submittedBy && currentUserEmail &&
        submittedBy.toLowerCase().trim() === currentUserEmail.toLowerCase().trim();
      const cannotApproveReason = isCreator
        ? 'You cannot approve a change you submitted'
        : null;

      return (
        <>
          <Tooltip title={cannotApproveReason || 'Approve'}>
            <span> {/* Wrapper needed for disabled button tooltip */}
              <IconButton
                size="small"
                color="success"
                onClick={() => handleOpenActionDialog(item, 'approve')}
                disabled={isCreator}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenActionDialog(item, 'reject')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isRateChange ? 'Preview Impact' : 'View Details'}>
            <IconButton size="small" onClick={openDetail}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      );
    }

    // Verifications (post-audit)
    if (category === 'verification') {
      if (item.status === 'pending_review') {
        return (
          <>
            <Tooltip title="Mark Verified">
              <IconButton size="small" color="success" onClick={() => handleOpenActionDialog(item, 'verify')}>
                <VerifiedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Query">
              <IconButton size="small" color="warning" onClick={() => handleOpenActionDialog(item, 'query')}>
                <QueryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reverse (Back to Draft)">
              <IconButton size="small" color="error" onClick={() => handleOpenActionDialog(item, 'reverse')}>
                <ReverseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={openDetail}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      }
      if (item.status === 'queried') {
        // Submitter can respond; reviewer can only view (until submitter responds)
        const isSubmitter = currentUserEmail &&
          item.requested_by?.toLowerCase().trim() === currentUserEmail.toLowerCase().trim();
        return (
          <>
            {isSubmitter && (
              <Tooltip title="Respond to Query">
                <IconButton size="small" color="primary" onClick={() => handleOpenActionDialog(item, 'respond')}>
                  <ReplyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View Details">
              <IconButton size="small" onClick={openDetail}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      }
      return null;
    }

    return null;
  };

  // True when the current user submitted the change (conflict of interest gate).
  const isCreatedBy = (item) => {
    const submittedBy = item?.requested_by || item?.change_data?.created_by;
    return submittedBy && currentUserEmail
      && submittedBy.toLowerCase().trim() === currentUserEmail.toLowerCase().trim();
  };

  // Rows the user can include in a bulk approve: pending status, not their own.
  const isSelectable = (item) =>
    item.status === 'pending'
    && !isCreatedBy(item)
    && canActOnTab(activeTab === 0 ? 'tab_contract_approvals' : 'tab_verifications');

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (rows) => {
    setSelectedIds((prev) => {
      const eligible = rows.filter(isSelectable).map(r => r.id);
      const allSelected = eligible.length > 0 && eligible.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        eligible.forEach((id) => next.delete(id));
      } else {
        eligible.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Open the bulk-approve confirmation dialog (captures shared comments).
  // Groups the current selection by cluster so the dialog can warn the user
  // when the batch spans more than one stat-card cluster.
  const handleBulkApproveOpen = () => {
    if (selectedIds.size === 0) return;
    const counts = new Map(); // key -> { key, label, count }
    pendingChanges.forEach((item) => {
      if (!selectedIds.has(item.id)) return;
      const c = clusterForChangeType(item.change_type);
      const entry = counts.get(c.key) || { key: c.key, label: c.label, count: 0 };
      entry.count += 1;
      counts.set(c.key, entry);
    });
    const clusters = Array.from(counts.values());
    setBulkApproveDialog({
      open: true,
      comments: '',
      clusters,
      mixed: clusters.length > 1,
      confirmInput: '',
    });
  };

  const closeBulkApproveDialog = () => {
    setBulkApproveDialog({ open: false, comments: '', clusters: [], mixed: false, confirmInput: '' });
  };

  // Approve every selected pending_change via the unified bulk-approve endpoint.
  // Each id runs in its own transaction server-side, so partial failures are
  // surfaced in the action banner without rolling back the successes. The
  // captured comments apply to every row in the batch.
  const handleBulkApproveConfirm = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      closeBulkApproveDialog();
      return;
    }
    const comments = bulkApproveDialog.comments;
    closeBulkApproveDialog();
    setLoading(true);
    setActionMessage(null);
    try {
      const response = await apiPost('/approvals/bulk-approve', {
        pending_change_ids: ids,
        comments: comments || null,
      });
      const { approved = [], failed = [] } = response?.data || {};
      if (failed.length === 0) {
        setActionMessage({ type: 'success', text: `Approved ${approved.length} change(s).` });
      } else {
        setActionMessage({
          type: 'warning',
          text: `Approved ${approved.length}, ${failed.length} failed: ${failed.map(f => `#${f.id} (${f.error})`).join('; ')}`,
        });
      }
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      setActionMessage({ type: 'error', text: err?.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  // Render table for pending items
  const renderTable = () => {
    if (loading) {
      return <LoadingSpinner />;
    }

    if (pendingChanges.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No pending items in this category.
        </Alert>
      );
    }

    const eligibleIds = pendingChanges.filter(isSelectable).map(r => r.id);
    const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.has(id));
    const someEligibleSelected = eligibleIds.some((id) => selectedIds.has(id));
    const showCheckboxes = statusFilter === 'pending' && canActOnTab(activeTab === 0 ? 'tab_contract_approvals' : 'tab_verifications');

    const renderItemRow = (item) => {
      const selectable = isSelectable(item);
      const checked = selectedIds.has(item.id);
      return (
              <TableRow
                key={item.id}
                hover
                onClick={() => (RATE_CHANGE_TYPES.has(item.change_type) ? handlePreviewRateChange(item) : handleViewDetails(item))}
                sx={{ cursor: 'pointer', bgcolor: checked ? 'action.selected' : undefined }}
              >
                {showCheckboxes && (
                  <TableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                      title={
                        !selectable
                          ? (isCreatedBy(item)
                              ? 'You cannot approve a change you submitted'
                              : 'This row is not selectable for bulk approve')
                          : ''
                      }
                    >
                      <span>
                        <Checkbox
                          size="small"
                          checked={checked}
                          disabled={!selectable}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectable) toggleRowSelected(item.id);
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell>
                  {item.entity_type === 'contract' ? (
                    <ContractReferenceCell
                      reference={item.entity_reference || `#${item.entity_id}`}
                      userReference={item.user_reference}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {item.entity_reference || `#${item.entity_id}`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {(() => {
                    const cluster = clusterForChangeType(item.change_type);
                    return (
                      <Chip
                        label={item.change_type.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                        sx={cluster.color ? {
                          borderColor: cluster.color,
                          color: cluster.color,
                          backgroundColor: `${cluster.color}14`,
                          fontWeight: 500,
                        } : undefined}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap>
                    {item.change_summary}
                  </Typography>
                </TableCell>
                {statusFilter === 'queried' && (
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {item.change_data?.contract_reference || '-'}
                    </Typography>
                  </TableCell>
                )}
                {statusFilter === 'queried' && (
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {item.after_snapshot?.reference || item.after_snapshot?.payment_reference || '-'}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={item.priority}
                    size="small"
                    color={getPriorityColor(item.priority)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.status.replace(/_/g, ' ')}
                    size="small"
                    color={getStatusColor(item.status)}
                  />
                </TableCell>
                <TableCell>{item.requested_by}</TableCell>
                {statusFilter === 'queried' && (
                  <TableCell>{item.reviewed_by || '-'}</TableCell>
                )}
                <TableCell>{formatDate(item.requested_at)}</TableCell>
                <TableCell>
                  {item.document_count > 0 && (
                    <Chip
                      icon={<AttachmentIcon />}
                      label={item.document_count}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {renderActionButtons(item)}
                  </Box>
                </TableCell>
              </TableRow>
      );
    };

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {showCheckboxes && (
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    size="small"
                    indeterminate={someEligibleSelected && !allEligibleSelected}
                    checked={allEligibleSelected}
                    disabled={eligibleIds.length === 0}
                    onChange={() => toggleSelectAllVisible(pendingChanges)}
                  />
                </TableCell>
              )}
              <SortableHeaderCell sort={sort} columnKey="entity_reference">Reference</SortableHeaderCell>
              <SortableHeaderCell sort={sort} columnKey="change_type">Type</SortableHeaderCell>
              <TableCell>Summary</TableCell>
              {statusFilter === 'queried' && <TableCell>Contract Ref</TableCell>}
              {statusFilter === 'queried' && <TableCell>Ext. Reference</TableCell>}
              <SortableHeaderCell sort={sort} columnKey="priority">Priority</SortableHeaderCell>
              <SortableHeaderCell sort={sort} columnKey="status">Status</SortableHeaderCell>
              <SortableHeaderCell sort={sort} columnKey="requested_by">Requested By</SortableHeaderCell>
              {statusFilter === 'queried' && <TableCell>Reviewed By</TableCell>}
              <SortableHeaderCell sort={sort} columnKey="requested_at">Requested At</SortableHeaderCell>
              <TableCell>Docs</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingChanges.map(renderItemRow)}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Get action dialog title and content
  const getActionDialogConfig = () => {
    switch (actionType) {
      case 'approve':
        return {
          title: 'Approve Change',
          description: 'Are you sure you want to approve this change? It will be executed immediately.',
          requireComments: false,
          buttonText: 'Approve',
          buttonColor: 'success',
        };
      case 'reject':
        return {
          title: 'Reject Change',
          description: 'Please provide a reason for rejecting this change.',
          requireComments: true,
          buttonText: 'Reject',
          buttonColor: 'error',
        };
      case 'verify':
        return {
          title: 'Mark as Verified',
          description: 'Confirm that this item has been reviewed and is correct.',
          requireComments: false,
          buttonText: 'Verify',
          buttonColor: 'success',
        };
      case 'query':
        return {
          title: 'Query Item',
          description: 'Flag this item for follow-up. Please describe what information is needed.',
          requireComments: true,
          buttonText: 'Submit Query',
          buttonColor: 'warning',
        };
      case 'reverse':
        return {
          title: 'Reverse to Draft',
          description: 'This will put the record back to Draft status for correction. Please provide a reason.',
          requireComments: true,
          buttonText: 'Reverse',
          buttonColor: 'error',
        };
      default:
        return {
          title: 'Confirm Action',
          description: '',
          requireComments: false,
          buttonText: 'Confirm',
          buttonColor: 'primary',
        };
    }
  };

  const actionConfig = getActionDialogConfig();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">Approvals</Typography>
          <Chip
            label={`${pendingChanges.length} items`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {/* Stat cards by change-type cluster (Contract Approvals tab only).
          Each card shows the live pending count from fn_get_pending_counts
          and doubles as the type filter — clicking sets typeFilter and
          highlights the active card with its accent border. */}
      {activeTab === 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {TYPE_FILTERS.map((f) => {
            const Icon = f.getIcon();
            return (
              <Grid key={f.key} size={{ xs: 12, sm: 6, md: 2.4 }}>
                <StatCard
                  title={f.label}
                  value={summary[f.countKey] ?? 0}
                  subtitle={f.subtitle}
                  icon={Icon}
                  color={f.color}
                  selected={typeFilter === f.key}
                  onClick={() => setTypeFilter(f.key)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ContractIcon fontSize="small" />
                <span>Contract Approvals</span>
                {summary.contract_approvals > 0 && (
                  <Chip label={summary.contract_approvals} size="small" color="primary" />
                )}
              </Box>
            }
            disabled={!canViewTab('tab_contract_approvals')}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedIcon fontSize="small" />
                <span>Verifications</span>
                {summary.verifications > 0 && (
                  <Chip label={summary.verifications} size="small" color="info" />
                )}
              </Box>
            }
            disabled={!canViewTab('tab_verifications')}
          />
        </Tabs>
      </Paper>
      {/* Status filter row — Search + Filter-by-status toggle on the left;
          when rows are selected, the bulk action controls appear on the
          right of the same row instead of dropping into a separate block. */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Reference, summary, type, requester..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Typography variant="body2" color="text.secondary">
          Filter by status:
        </Typography>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              setStatusFilter(newValue);
            }
          }}
          size="small"
        >
          <ToggleButton value="pending">
            Pending
          </ToggleButton>
          {activeTab === 1 && (
            <ToggleButton value="queried">
              Queried
            </ToggleButton>
          )}
          <ToggleButton value="executed">
            Executed
          </ToggleButton>
          <ToggleButton value="rejected">
            Rejected
          </ToggleButton>
        </ToggleButtonGroup>
        {selectedIds.size > 0 && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {selectedIds.size} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleBulkApproveOpen}
              disabled={loading}
            >
              Approve Selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedIds(new Set())}
              disabled={loading}
            >
              Clear Selection
            </Button>
          </>
        )}
      </Box>
      {/* Action message banner */}
      {actionMessage && (
        <Alert severity={actionMessage.type} sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage.text}
        </Alert>
      )}
      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {renderTable()}
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        {renderTable()}
      </TabPanel>
      {/* Rate Change Preview Modal — opens when the eye icon is clicked on
          an adhoc_rate_change or bulk_rate_change row. Approve/Reject inside
          the modal route through the unified approve/reject action dialog. */}
      <RateChangePreviewModal
        open={rateChangePreviewDialog.open}
        onClose={() => setRateChangePreviewDialog({ open: false, rateChangeId: null, currencyCode: 'ZAR' })}
        rateChangeId={rateChangePreviewDialog.rateChangeId}
        currencyCode={rateChangePreviewDialog.currencyCode}
        showApprove={selectedItem?.status === 'pending' && canActOnTab('tab_contract_approvals')}
        onApprove={async () => {
          if (!selectedItem) return;
          setRateChangePreviewDialog({ open: false, rateChangeId: null, currencyCode: 'ZAR' });
          handleOpenActionDialog(selectedItem, 'approve');
        }}
      />
      {/* Detail Modal */}
      {selectedItem && detailModalOpen && (
        <ApprovalDetailModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          onAction={(action, comments) => {
            handleExecuteAction(action, comments || '');
          }}
          permissions={permissions}
        />
      )}
      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{actionConfig.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionConfig.description}
          </Typography>

          {selectedItem && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{selectedItem.entity_reference || `#${selectedItem.entity_id}`}</strong>
              {' - '}
              {selectedItem.change_summary}
            </Alert>
          )}

          {actionMessage && (
            <Alert severity={actionMessage.type} sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
              {actionMessage.text}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionConfig.requireComments ? 'Comments (Required)' : 'Comments (Optional)'}
            value={actionComments}
            onChange={(e) => setActionComments(e.target.value)}
            error={actionConfig.requireComments && !actionComments.trim()}
            helperText={actionConfig.requireComments && !actionComments.trim() ? 'Comments are required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionConfig.buttonColor}
            onClick={() => handleExecuteAction()}
            disabled={actionLoading || (actionConfig.requireComments && !actionComments.trim())}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionConfig.buttonText}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Bulk Approve Confirmation Dialog — comments apply to every row in
          the batch. When the selection spans more than one cluster the user
          must type the total count to confirm before Approve enables. */}
      <Dialog
        open={bulkApproveDialog.open}
        onClose={closeBulkApproveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve {selectedIds.size} Change{selectedIds.size === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each selected change will be executed individually. Partial failures are reported back.
          </Typography>
          {bulkApproveDialog.mixed && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                This selection spans {bulkApproveDialog.clusters.length} change-type clusters:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {bulkApproveDialog.clusters.map((c) => (
                  <li key={c.key}>
                    <Typography variant="body2">
                      {c.label} ({c.count})
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Each change runs its own executor — please confirm you intend to approve them together.
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (Optional)"
            value={bulkApproveDialog.comments}
            onChange={(e) => setBulkApproveDialog((prev) => ({ ...prev, comments: e.target.value }))}
            autoFocus
          />
          {bulkApproveDialog.mixed && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={`Type ${selectedIds.size} to confirm`}
              placeholder={String(selectedIds.size)}
              value={bulkApproveDialog.confirmInput}
              onChange={(e) => setBulkApproveDialog((prev) => ({ ...prev, confirmInput: e.target.value }))}
              helperText={`Type the number ${selectedIds.size} to enable Approve.`}
              error={
                bulkApproveDialog.confirmInput.length > 0
                && bulkApproveDialog.confirmInput.trim() !== String(selectedIds.size)
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkApproveDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkApproveConfirm}
            disabled={
              loading
              || (bulkApproveDialog.mixed
                && bulkApproveDialog.confirmInput.trim() !== String(selectedIds.size))
            }
            startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Approve {selectedIds.size}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApprovalsPage;
